import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';

import Grid from '@material-ui/core/Grid';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';

import { ViewMode } from './viewModes';
import EmployerModal from './EmployerModal';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import EmployerIcon from 'components/Icons/EmployerIcon';
import Info from 'components/Tooltip/Info';

import { EmployersStore } from 'stores/workflowStores/finances/employers/employersStore';
import { EmployersData } from 'stores/workflowStores/finances/employers/model';
import { RootStore } from 'stores/rootStore';
import useStyles from './styles/moduleStyles';
import { EdwardColors } from 'enums';

interface Props {
  accountId: string;
  rootStore: RootStore;
  openEdit?: () => void;
  readOnly?: boolean;
  viewMode?: ViewMode;
  errors?: string[];
  refresh?: Function;
}

const Employers: React.FC<Props> = ({ accountId, rootStore, readOnly, viewMode, openEdit, errors, refresh }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [isEmployerOpen, setIsEmployerOpen] = useState(false);
  const [employerId, setEmployerId] = useState<string | undefined>(undefined);
  const [employersStore] = useState(new EmployersStore(rootStore, accountId, viewMode));
  const [displayError, setDisplayError] = useState<string[] | undefined>(errors);

  useEffect(() => {
    employersStore.fetchEmployersData();
  }, [employersStore]);

  useEffect(() => {
    setDisplayError(errors);
  }, [errors]);

  const handleBankAccountClose = () => {
    setIsEmployerOpen(false);
  };

  const editEmployer = (bankAccountId: string) => {
    setEmployerId(bankAccountId);
    setIsEmployerOpen(true);
  };

  const handleRefresh = () => {
    employersStore.fetchEmployersData();
    setDisplayError(undefined);
    refresh && refresh();
  };

  const [confirmDialog, setConfirmDialog] = useState<string | undefined>(undefined);

  const listEmployers = (data: EmployersData) => {
    return (
      <>
        {data.employers.map((employer) => {
          return (
            <Grid container item xs={12} spacing={1} key={employer.companyNumber}>
              <Grid
                container
                item
                alignContent="center"
                alignItems="center"
                className={`${classes.item} ${classes.bankAccount}`}
              >
                <Grid item xs={2} sm={2} xl={1} className={classes.icon}>
                  <EmployerIcon fontSize="inherit" />
                </Grid>
                <Grid item xs={8} xl={9}>
                  {t('employer_companyName')}: <strong>{employer.companyName}</strong>, {t('employer_companyNumber')}:{' '}
                  <strong>{employer.companyNumber}</strong>
                </Grid>
                <Grid item xs={2} className={classes.actions}>
                  <IconButton
                    size="medium"
                    className="accountEdit-button"
                    onClick={() => editEmployer(employer.companyNumber!)}
                    disabled={readOnly}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="medium"
                    className="account-delete-button"
                    onClick={() => {
                      setConfirmDialog(employer.companyNumber);
                    }}
                    disabled={readOnly}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        {data.addingEnabled && (
          <Grid container item xs={12} spacing={1}>
            <Grid
              container
              item
              className={`${classes.item} ${classes.addBankAccount}`}
              alignContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                color="primary"
                id="additional-target-button"
                onClick={async () => {
                  openEdit !== undefined && readOnly && (await openEdit());
                  setEmployerId(undefined);
                  setIsEmployerOpen(true);
                }}
                startIcon={<AddIcon fontSize="medium" />}
                disabled={readOnly}
              >
                {t('employer_add')}
              </Button>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  return employersStore.employersData ? (
    <>
      <EmployerModal
        opened={isEmployerOpen}
        handleClose={handleBankAccountClose}
        employerId={employerId}
        employersStore={employersStore}
        refresh={handleRefresh}
        detailMode={viewMode !== ViewMode.WF}
      />

      <ConfirmDialog
        noHeader
        isOpen={confirmDialog !== undefined}
        confirm={async () => {
          await employersStore?.deleteEmployer(confirmDialog!);
          setConfirmDialog(undefined);
          handleRefresh();
        }}
        close={() => {
          setConfirmDialog(undefined);
        }}
      >
        {t(`employer_confirm_delete`)}
      </ConfirmDialog>

      <Typography variant="h3">
        {t('employers')}
        <Info content={t('employers_tooltip')} heading />
      </Typography>
      <Typography>{t('employers_info')}</Typography>
      {displayError && <p style={{ color: EdwardColors.ALERT }}>{displayError.map((err) => t(err))}</p>}
      {listEmployers(employersStore.employersData)}
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Employers);
